import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../UI/Card';
import { useEPMContext } from '../../../contexts/EPMContext';
import { useUserContext } from '../../../contexts/UserContext';
import { permission } from '../../../config/permission';
import { COLUMN_CONFIG } from '../TestingBoard/constants';

const PipelineSummary = () => {
    const navigate = useNavigate();
    const { tests = [], loading } = useEPMContext();
    const { checkPermission } = useUserContext();
    const canViewTests = checkPermission(permission.XCM_TESTS_VIEW);

    // Count tests per stage
    const stageCounts = useMemo(() => {
        const counts = {};
        tests.forEach((test) => {
            const status = test.status === 'IDEA' ? 'IDEAS' : test.status;
            counts[status] = (counts[status] || 0) + 1;
        });
        return counts;
    }, [tests]);

    // Summary caption (conversion rate & total tests)
    const caption = useMemo(() => {
        if (!tests.length) {
            return loading ? 'Loading pipeline data…' : '0 tests in pipeline · Add tests in the Testing Board';
        }

        const liveCount = (stageCounts.LIVE || 0) + (stageCounts.ANALYSING || 0) + (stageCounts.CONCLUDED || 0);
        const liveRate = Math.round((liveCount / tests.length) * 100);
        const testInPipeline = tests.filter((test) => test.status !== 'CONCLUDED').length;

        return `${liveRate}% of ideas reach live · ${testInPipeline} tests in pipeline`;
    }, [tests.length, stageCounts, loading]);

    return (
        <Card variant="accent" padding="md" className="mb-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
                {COLUMN_CONFIG.map(({ key, label }) => (
                    <div
                        key={key}
                        className={`flex flex-col py-3 px-4 first:pl-0 last:pr-0 ${
                            canViewTests
                                ? 'cursor-pointer hover:bg-slate-50/80 rounded-lg transition-all group'
                                : ''
                        }`}
                        onClick={canViewTests ? () => navigate('/epm/testing-board') : undefined}
                    >
                        <strong className="block text-2xl md:text-3xl font-extrabold text-primary tracking-tight transition-transform group-hover:-translate-y-0.5">
                            {stageCounts[key] || 0}
                        </strong>
                        <span className="text-xs text-gray-500 font-medium mt-1">
                            {label}
                        </span>
                    </div>
                ))}
            </div>

            <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between flex-wrap gap-2">
                <span className="stride-badge-pill stride-badge-pill-primary">
                    {caption}
                </span>
            </div>
        </Card>
    );
};

export default PipelineSummary;
